/**
 * PATH: packages/auth/src/rbac-request-coalesce.ts
 * FILE_ID: AUTH-RBAC-COALESCE-001
 * PURPOSE: In-flight request coalescing for the RBAC permissions fetch.
 * WHY: useRBAC() runs independently in every component that needs permissions,
 *      with no shared cache. A page that mounts N permission-gated components
 *      therefore fired N identical concurrent `GET /api/rbac/permissions/`
 *      requests (measured: 5 simultaneous on the tenant dashboard, all within
 *      ~5ms). This collapses concurrent in-flight requests for the same key
 *      into a single shared promise — N callers, 1 network request.
 * FLOW:
 *   ┌──────────┐   in-flight for key?   ┌─────────────────────┐
 *   │ caller N │ ─────── yes ─────────→ │ return shared promise │
 *   └──────────┘                        └─────────────────────┘
 *        │ no
 *        ▼
 *   start fn() → track by key → clear tracking on settle → return promise
 *
 * Deliberately does NOT cache results: once the shared request settles the key
 * is released, so post-resolution behaviour (refresh, refetch on user/org
 * change, error fallback) is byte-for-byte identical to the un-coalesced path.
 * This keeps the change safe for a security-critical package — it only removes
 * redundant *simultaneous* network calls, never serves stale permissions.
 * RELATED:
 *   - AUTH-RBAC-STATE-001 (rbac-state.ts): the sole consumer (useRBAC).
 */

const inFlight = new Map<string, Promise<unknown>>()

/**
 * Run `fn` unless an identical request (same `key`) is already in flight, in
 * which case the existing promise is returned and `fn` is NOT invoked again.
 *
 * @param key  Identity of the request — callers with the same key share one
 *             call. For RBAC this is `${userEmail}::${organisationId}` so a
 *             different user or org never shares another's permissions.
 * @param fn   Producer of the underlying promise (e.g. the network fetch).
 * @returns    A promise resolving/rejecting with `fn`'s result. The key is
 *             released once settled (success OR failure) so failures never
 *             poison subsequent fetches.
 */
export function coalesceRequest<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined
  if (existing) return existing

  const promise = fn()
  inFlight.set(key, promise)

  const release = () => {
    // Only delete if it's still our promise — a later, distinct call for the
    // same key may have replaced it after we settled.
    if (inFlight.get(key) === promise) inFlight.delete(key)
  }
  // Attach a settled handler purely for cleanup. This must not swallow the
  // result: the original `promise` is what we return to callers, so their
  // `.then`/`.catch`/`await` still observe the real value/error.
  promise.then(release, release)

  return promise
}

/**
 * Number of requests currently in flight. Test/diagnostics only.
 */
export function inFlightCount(): number {
  return inFlight.size
}

/**
 * Clear all in-flight tracking. Test-only — never call in app code.
 */
export function __resetCoalesceState(): void {
  inFlight.clear()
}
